// Lightweight client telemetry beacon. Captures client-side failures the server
// cannot otherwise see — AI generation failures, fetch/network drops, GraphQL
// errors, forced logouts — and POSTs them (batched) to the /telemetry endpoint,
// authenticated with the Cognito ID token. It must NEVER throw and NEVER block
// the app: every path is wrapped and failures are swallowed. Cognito is imported
// lazily (inside flush) so importing this module pulls in no heavy auth deps —
// keeps it safe to import from the store and unit tests.

// Derive the telemetry URL from the chat endpoint (same private API + authorizer),
// so no extra env var / pipeline wiring is needed.
const TELEMETRY_URL = (process.env.VUE_APP_CHAT_API_URL || '').replace(/\/chat\/?$/, '/telemetry')

const rand = () => Math.random().toString(36).slice(2)
const sessionId = `s_${rand()}_${Date.now()}`
const MAX_QUEUE = 20
const FLUSH_MS = 4000

let queue = []
let flushTimer = null

// A correlation id ties a single user action (e.g. one generation run) together
// across the telemetry events it produces. Callers create one and pass it in.
export const newCorrelationId = () => `c_${rand()}_${Date.now()}`

const flush = async (useKeepalive = false) => {
  if (!TELEMETRY_URL || queue.length === 0) {
    return
  }
  const batch = queue
  queue = []
  let token = ''
  try {
    const { default: cognitoAuth } = await import('@/cognito')
    token = await cognitoAuth.getIdTokenPromise()
  } catch (e) {
    // no token — send anonymously; the endpoint still records it
  }
  try {
    await fetch(TELEMETRY_URL, {
      method: 'POST',
      keepalive: useKeepalive,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: token } : {}) },
      body: JSON.stringify(batch)
    })
  } catch (e) {
    // telemetry must never affect the app — drop on failure
  }
}

const scheduleFlush = () => {
  if (flushTimer) {
    return
  }
  flushTimer = setTimeout(() => {
    flushTimer = null
    flush()
  }, FLUSH_MS)
}

// Flush the queue immediately, surviving an imminent navigation (keepalive). Call
// this right before a forced logout / redirect: an SPA route change does not fire
// pagehide, so the normal debounced flush would lose the last events (e.g. the
// session_logout beacon). Fire-and-forget — never awaited, never throws.
export const flushNow = () => {
  try {
    if (flushTimer) {
      clearTimeout(flushTimer)
      flushTimer = null
    }
    flush(true)
  } catch (e) {
    // never throw from telemetry
  }
}

export const track = (type, data = {}) => {
  try {
    queue.push({
      type,
      ts: new Date().toISOString(),
      sessionId,
      url: typeof location !== 'undefined' ? location.pathname : '',
      online: typeof navigator !== 'undefined' ? navigator.onLine : null,
      ...data
    })
    if (queue.length >= MAX_QUEUE) {
      flush()
    } else {
      scheduleFlush()
    }
  } catch (e) {
    // never throw from telemetry
  }
}

export const initTelemetry = () => {
  if (typeof window === 'undefined') {
    return
  }
  window.addEventListener('error', (e) => {
    track('window_error', { message: String(e?.message || '').slice(0, 500), src: e?.filename })
  })
  window.addEventListener('unhandledrejection', (e) => {
    const msg = e?.reason?.message || String(e?.reason || '')
    track('unhandled_rejection', { message: String(msg).slice(0, 500) })
  })
  window.addEventListener('online', () => track('network_online'))
  window.addEventListener('offline', () => track('network_offline'))
  // Flush on the way out so we don't lose the last events.
  window.addEventListener('pagehide', () => flush(true))
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush(true)
    }
  })
  track('session_start', { ua: typeof navigator !== 'undefined' ? navigator.userAgent : '' })
}
